// @spec BCP-STR-026
// Create new tabletop exercise page
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateExercise } from '@/features/bcp/api/exercises';
import { CreateExerciseForm } from '@/features/bcp/components/CreateExerciseForm';
import type { CreateExerciseRequest } from '@/features/bcp/types/exercises';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/bcp/exercises/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.newTabletopExercise'),
  }),
  component: NewExercisePage,
});

function NewExercisePage() {
  const { t } = useTranslation('bcp');
  const navigate = useNavigate();
  const createExercise = useCreateExercise();

  const handleBack = () => {
    void navigate({ to: '/bcp/exercises' });
  };

  const handleSubmit = (data: CreateExerciseRequest) => {
    createExercise.mutate(data, {
      onSuccess: () => {
        toast.success(t('bcp:toast.exerciseCreatedSuccessfully'));
        void navigate({ to: '/bcp/exercises' });
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : i18n.t('bcp:toast.failedToCreateExercise'));
      },
    });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('exercises.backToExercises')}
          </Button>
          <h1 className="text-2xl font-semibold">{t('exercises.createPageTitle')}</h1>
          <p className="text-muted-foreground">
            {t('exercises.createPageDescription')}
          </p>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('exercises.exerciseDetails')}</CardTitle>
            <CardDescription>
              {t('exercises.exerciseDetailsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CreateExerciseForm
              onSubmit={handleSubmit}
              onCancel={handleBack}
              isSubmitting={createExercise.isPending}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
